import { useState } from "react";
import { Copy, Check, Inbox, Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 animate-fade-up">
    <div>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 mb-2">{eyebrow}</p>}
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  gray: "bg-slate-100 text-slate-600 border-slate-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const STATUS_TONE = {
  active: "green", verified: "green", success: "green", available: "green",
  suspended: "red", failed: "red", failure: "red", disabled: "gray", invalid: "red",
  trial: "amber", pending: "amber", invited: "amber", beta: "indigo",
  expired: "gray", not_configured: "gray", unavailable: "gray",
};

export const Tag = ({ tone = "gray", children, className, ...rest }) => (
  <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", TONES[tone], className)} {...rest}>
    {children}
  </span>
);

export const StatusBadge = ({ status, prefix = "status", testid }) => (
  <Tag tone={STATUS_TONE[status] || "gray"} data-testid={testid}>
    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
    {t(`${prefix}.${status}`, status)}
  </Tag>
);

export const Panel = ({ title, actions, children, className, testid }) => (
  <section className={cn("bg-white rounded-xl border border-slate-200/80 shadow-sm", className)} data-testid={testid}>
    {(title || actions) && (
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        {actions}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const EmptyState = ({ icon: Icon = Inbox, title, text, action, testid = "empty-state" }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testid}>
    <div className="h-14 w-14 rounded-2xl bg-sky-50 border border-sky-100 flex items-center justify-center mb-4">
      <Icon className="h-6 w-6 text-sky-600" />
    </div>
    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    {text && <p className="text-sm text-slate-500 mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const RowsSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3 p-5" data-testid="loading-skeleton">
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} className="h-10 w-full" />
    ))}
  </div>
);

export const Pager = ({ page, pageSize, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-sm text-slate-500">
      <span data-testid="pagination-total">{total} résultat{total > 1 ? "s" : ""}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pagination-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-mono text-xs" data-testid="pagination-current">{page} / {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pagination-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const SearchBox = ({ value, onChange, placeholder = "Rechercher…", testid = "search-input" }) => (
  <div className="relative w-full sm:w-72">
    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9 bg-white" data-testid={testid} />
  </div>
);

export const Field = ({ label, error, hint, required, children, className }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <Label className="text-xs font-semibold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
    )}
    {children}
    {error ? <p className="text-xs text-red-600" data-testid="field-error">{error}</p> : hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
  </div>
);

export const CopyField = ({ value, testid = "copy-field" }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setDone(true);
    toast.success("Lien copié");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex gap-2">
      <Input readOnly value={value} className="font-mono text-xs bg-slate-50" data-testid={testid} />
      <Button type="button" variant="outline" onClick={copy} data-testid={`${testid}-copy`}>
        {done ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
};

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Confirmer", onConfirm, destructive, children, busy, disabled }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      {children}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-dialog-cancel">Annuler</AlertDialogCancel>
        <AlertDialogAction
          onClick={(e) => {
            e.preventDefault();
            onConfirm();
          }}
          disabled={busy || disabled}
          className={destructive ? "bg-red-600 hover:bg-red-700" : ""}
          data-testid="confirm-dialog-confirm"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Kpi = ({ label, value, icon: Icon, tone = "sky", testid, hint }) => (
  <div className="p-5 bg-white rounded-xl border border-slate-200/80 shadow-sm relative overflow-hidden group hover:border-sky-300 transition-colors" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && (
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center border", TONES[tone])}>
          <Icon className="h-4 w-4" />
        </div>
      )}
    </div>
    <p className="text-3xl font-extrabold text-slate-900 mt-3 font-heading" data-testid={testid ? `${testid}-value` : undefined}>{value ?? "—"}</p>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export const Spinner = ({ className }) => <Loader2 className={cn("h-5 w-5 animate-spin text-sky-600", className)} />;

export const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loader">
    <Spinner className="h-7 w-7" />
  </div>
);
